import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';

/**
 * Newsletter signup box for any page:
 *   m(app.vouchaNewsletter.SignupBox, { source: 'calculator-tax', categories: ['weekly-finance'], fixed: true })
 *   m(app.vouchaNewsletter.SignupBox, { source: 'footer', preselect: ['weekly-finance'] })   // with choices
 * `fixed`: subscribe to the given newsletter(s) only, with no choices shown, and
 * stay hidden for people who already get them.
 * Visitors and members subscribe in one click; the box confirms it on the page.
 */
export default class SignupBox extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.categories = null;
    this.chosen = {};
    this.email = '';
    this.website = ''; // honeypot
    this.sending = false;
    this.done = null;
    this.error = null;
    this.member = null; // member's current state

    const api = app.forum.attribute('apiUrl');
    const preselect = this.attrs.categories || this.attrs.preselect || [];
    this.fixed = !!this.attrs.fixed;
    this.fixedIds = [];
    const settle = () => {
      if (this.fixed) this.fixedIds = this.categories.filter(c => this.matches(c, preselect)).map(c => c.id);
    };
    if (app.session.user) {
      app.request({
        method: 'GET',
        url: api + '/voucha-newsletter/me',
        errorHandler: () => {}
      }).then(state => {
        this.member = state;
        this.categories = state.categories;
        state.categories.forEach(c => this.chosen[c.id] = state.subscribed ? c.selected : c.selected || this.matches(c, preselect));
        settle();
      }).catch(() => this.categories = []).finally(() => m.redraw());
    } else {
      app.request({
        method: 'GET',
        url: api + '/voucha-newsletter/public-categories',
        errorHandler: () => {}
      }).then(r => {
        this.categories = r.categories;
        r.categories.forEach(c => this.chosen[c.id] = preselect.length ? this.matches(c, preselect) : c.isDefault);
        settle();
      }).catch(() => this.categories = []).finally(() => m.redraw());
    }
  }
  storageKey() {
    return 'voucha-newsletter-joined:' + this.fixedIds.slice().sort().join(',');
  }
  alreadyHasFixed() {
    if (!this.fixed || !this.fixedIds.length) return false;
    if (this.member) {
      return this.member.subscribed && this.fixedIds.every(id => (this.member.categories.find(c => c.id === id) || {}).selected);
    }
    try {
      return localStorage.getItem(this.storageKey()) === '1';
    } catch (e) {
      return false;
    }
  }
  matches(category, preselect) {
    return preselect.some(p => (category.slug || '').includes(p) || (category.name || '').toLowerCase().includes(p.replace(/-/g, ' ')));
  }
  view() {
    if (!this.categories || !this.categories.length) return null;
    if (this.member && this.member.excluded) return null;
    if (this.fixed && !this.fixedIds.length) return null;
    if (!this.done && this.alreadyHasFixed()) return null;
    const single = this.fixed && this.fixedIds.length === 1 ? this.categories.find(c => c.id === this.fixedIds[0]) : null;
    const title = this.attrs.title || (single ? 'Get the ' + single.name : 'Get the Voucha newsletters');
    const text = this.attrs.text || single && single.description || 'Naira, markets, tax and diaspora news that matters to your money. Free, and you can unsubscribe any time.';
    if (this.done) {
      return m("div", {
        className: "VouchaSignup is-done"
      }, m("strong", null, this.done.title), m("p", null, this.done.text));
    }
    if (!this.fixed && this.member && this.member.subscribed) {
      const names = this.member.categories.filter(c => c.selected).map(c => c.name);
      return m("div", {
        className: "VouchaSignup is-done"
      }, m("strong", null, "You're subscribed"), m("p", null, "You get ", names.length ? names.join(', ') : 'Voucha newsletters', ". Change them in", ' ', m("a", {
        href: app.route('settings')
      }, "your settings"), "."));
    }
    return m("div", {
      className: "VouchaSignup"
    }, m("strong", {
      className: "VouchaSignup-title"
    }, title), m("p", {
      className: "VouchaSignup-text"
    }, text), m("div", {
      className: "VouchaSignup-options",
      style: this.fixed ? 'display:none' : ''
    }, this.categories.map(c => m("label", {
      className: "VouchaSignup-option",
      title: c.description || ''
    }, m("input", {
      type: "checkbox",
      checked: !!this.chosen[c.id],
      onchange: e => this.chosen[c.id] = e.target.checked
    }), " ", c.name))), m("form", {
      className: "VouchaSignup-form",
      onsubmit: e => {
        e.preventDefault();
        this.submit();
      }
    }, !this.member ? m("input", {
      className: "FormControl",
      type: "email",
      required: true,
      placeholder: "Your email address",
      autocomplete: "email",
      value: this.email,
      oninput: e => this.email = e.target.value
    }) : null, m("div", {
      className: "VouchaSignup-hp",
      "aria-hidden": "true"
    }, m("input", {
      tabindex: "-1",
      autocomplete: "off",
      value: this.website,
      oninput: e => this.website = e.target.value
    })), window.VouchaTurnstile && !this.member ? m(window.VouchaTurnstile, {
      action: 'newsletter',
      onToken: t => this.turnstile = t,
      bind: w => this.turnstileWidget = w
    }) : null, m(Button, {
      type: "submit",
      className: "Button Button--primary",
      loading: this.sending
    }, "Subscribe")), this.error ? m("p", {
      className: "VouchaSignup-error"
    }, this.error) : null, !this.member ? m("p", {
      className: "VouchaSignup-small"
    }, "No spam. Unsubscribe in one click from any newsletter.") : null);
  }
  async submit() {
    let categoryIds = this.fixed ? this.fixedIds.slice() : this.categories.filter(c => this.chosen[c.id]).map(c => c.id);
    if (this.fixed && this.member && this.member.subscribed) {
      // Add to what they already get, never replace it
      const current = this.member.categories.filter(c => c.selected).map(c => c.id);
      categoryIds = Array.from(new Set([...current, ...categoryIds]));
    }
    if (!categoryIds.length) {
      this.error = 'Tick at least one newsletter.';
      return;
    }
    this.sending = true;
    this.error = null;
    m.redraw();
    const api = app.forum.attribute('apiUrl');
    try {
      if (this.member) {
        await app.request({
          method: 'POST',
          url: api + '/voucha-newsletter/me',
          body: {
            subscribed: true,
            categoryIds
          },
          errorHandler: () => {}
        });
        this.done = {
          title: "You're subscribed",
          text: 'Your first newsletter will arrive with the next issue. Change them any time in your settings.'
        };
      } else {
        await app.request({
          method: 'POST',
          url: api + '/voucha-newsletter/subscribe',
          body: {
            email: this.email,
            categoryIds,
            source: this.attrs.source || 'signup-box',
            website: this.website,
            turnstile: this.turnstile || ''
          },
          errorHandler: () => {}
        });
        const names = this.categories.filter(c => this.fixed ? this.fixedIds.includes(c.id) : this.chosen[c.id]).map(c => c.name);
        try {
          if (this.fixed) localStorage.setItem(this.storageKey(), '1');
        } catch (e) {}
        this.done = {
          title: "You're subscribed",
          text: this.email + ' will get ' + names.join(', ') + ', starting with the next issue. Every email has a one-click unsubscribe link.'
        };
      }
    } catch (e) {
      if (this.turnstileWidget) this.turnstileWidget.reset(); // each check works once
      this.error = e?.response?.message || 'That didn\'t work. Please try again.';
    }
    this.sending = false;
    m.redraw();
  }
}
flarum.reg.add('voucha-newsletter', 'forum/components/SignupBox', SignupBox);